import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, PackageX, ChevronDown, ChevronUp } from 'lucide-react';
import { invoiceIntakeService } from '@/lib/invoiceIntakeService';
import { useCompany } from '@/context/CompanyContext';

type Discrepancy = {
  id: string; invoice_id: string; line_id: string; company_id: string;
  discrepancy_type: string; quantity_expected: number; quantity_actual: number;
  amount_difference: number; resolution_status: string; credit_note_number: string;
  credit_amount: number; resolved_at: string; resolved_by: string;
  notes: string; created_at: string; updated_at: string;
  supplier_invoices: { supplier_name_raw: string; invoice_number: string };
};

const TYPE_LABELS: Record<string, string> = { shortage: 'חוסר', surplus: 'עודף', damage: 'נזק', price_mismatch: 'פער מחיר', other: 'אחר' };

const STATUS_CONFIG: Record<string, { label: string; cls: string }> = {
  open: { label: 'פתוח', cls: 'bg-red-100 text-red-700' },
  credit_requested: { label: 'ביקשנו זיכוי', cls: 'bg-amber-100 text-amber-700' },
  credit_received: { label: 'זיכוי התקבל', cls: 'bg-blue-100 text-blue-700' },
  resolved: { label: 'טופל', cls: 'bg-green-100 text-green-700' },
  written_off: { label: 'נמחק', cls: 'bg-gray-100 text-gray-600' },
};

const TABS = [
  { key: 'all', label: 'הכל' },
  { key: 'open', label: 'פתוח' },
  { key: 'credit_requested', label: 'ביקשנו זיכוי' },
  { key: 'credit_received', label: 'זיכוי התקבל' },
  { key: 'resolved', label: 'טופל' },
  { key: 'written_off', label: 'נמחק' },
];

export function InvoiceDiscrepanciesPage() {
  const { t } = useTranslation();
  const { activeCompany } = useCompany();
  const [items, setItems] = useState<Discrepancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    const statusArg = filter === 'all' ? undefined : filter;
    const { data } = await invoiceIntakeService.getDiscrepancies(activeCompany.id, statusArg);
    setItems((data as Discrepancy[]) ?? []);
    setLoading(false);
  }, [activeCompany?.id, filter]);

  useEffect(() => { load(); }, [load]);

  const openCount = items.filter(d => d.resolution_status === 'open').length;
  const creditCount = items.filter(d => d.resolution_status === 'credit_requested').length;
  const resolvedCount = items.filter(d => d.resolution_status === 'resolved').length;

  const toggleExpand = (d: Discrepancy) => {
    if (expandedId === d.id) { setExpandedId(null); return; }
    setExpandedId(d.id);
    setEditStatus(d.resolution_status);
    setEditNotes(d.notes ?? '');
  };

  const handleSave = async () => {
    if (!expandedId) return;
    setSaving(true);
    await invoiceIntakeService.updateDiscrepancyStatus(expandedId, editStatus, editNotes);
    setSaving(false);
    setExpandedId(null);
    load();
  };

  const StatusBadge = ({ status }: { status: string }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['open']!;
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>{cfg.label}</span>;
  };

  const summaryCards = [
    { label: 'פתוחים', count: openCount, cls: 'bg-red-50 border-red-200 text-red-700' },
    { label: 'בקשת זיכוי', count: creditCount, cls: 'bg-amber-50 border-amber-200 text-amber-700' },
    { label: 'טופלו', count: resolvedCount, cls: 'bg-green-50 border-green-200 text-green-700' },
  ];

  const InlineEdit = () => (
    <div className="p-4 bg-gray-50 border-t border-gray-200 space-y-3">
      <div className="flex flex-col sm:flex-row gap-3">
        <select value={editStatus} onChange={e => setEditStatus(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
          {Object.entries(STATUS_CONFIG).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)}
          placeholder={t('invoiceIntake.discrepancies.notesPlaceholder', 'הערות...')}
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" rows={2} />
      </div>
      <div className="flex gap-2 justify-start">
        <button onClick={handleSave} disabled={saving}
          className="px-4 py-1.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50">
          {saving ? t('common.saving', 'שומר...') : t('common.save', 'שמור')}
        </button>
        <button onClick={() => setExpandedId(null)}
          className="px-4 py-1.5 bg-white border border-gray-300 text-sm rounded-lg hover:bg-gray-50">
          {t('common.cancel', 'ביטול')}
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6" dir="rtl">
      <div className="flex items-center gap-4">
        <div className="p-3 bg-teal-100 rounded-xl">
          <AlertTriangle className="w-8 h-8 text-teal-700" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('invoiceIntake.discrepancies.title', 'חוסרים ופערים')}
        </h1>
      </div>

      {!loading && items.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {summaryCards.map(c => (
            <div key={c.label} className={`rounded-xl border p-4 text-center ${c.cls}`}>
              <div className="text-2xl font-bold">{c.count}</div>
              <div className="text-sm mt-1">{c.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-1 flex-wrap border-b border-gray-200 pb-1">
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => { setFilter(tab.key); setExpandedId(null); }}
            className={`px-3 py-1.5 text-sm rounded-t-lg transition-colors ${
              filter === tab.key ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin mb-2" />
          <span className="text-sm">{t('common.loading', 'טוען...')}</span>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <PackageX className="w-12 h-12 mb-3" />
          <span className="text-sm">{t('invoiceIntake.discrepancies.empty', 'אין פערים להצגה')}</span>
        </div>
      ) : (
        <>
          <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-teal-50 text-teal-800">
                <tr>
                  <th className="px-4 py-3 text-right font-medium">ספק</th>
                  <th className="px-4 py-3 text-right font-medium">מס׳ חשבונית</th>
                  <th className="px-4 py-3 text-right font-medium">סוג</th>
                  <th className="px-4 py-3 text-right font-medium">צפוי</th>
                  <th className="px-4 py-3 text-right font-medium">בפועל</th>
                  <th className="px-4 py-3 text-right font-medium">הפרש</th>
                  <th className="px-4 py-3 text-right font-medium">סטטוס</th>
                  <th className="px-4 py-3 w-8" />
                </tr>
              </thead>
              {items.map(d => (
                <tbody key={d.id}>
                  <tr onClick={() => toggleExpand(d)}
                    className={`border-t border-gray-100 cursor-pointer hover:bg-gray-50 ${expandedId === d.id ? 'bg-teal-50/40' : ''}`}>
                    <td className="px-4 py-3">{d.supplier_invoices?.supplier_name_raw}</td>
                    <td className="px-4 py-3">{d.supplier_invoices?.invoice_number}</td>
                    <td className="px-4 py-3">{TYPE_LABELS[d.discrepancy_type] ?? d.discrepancy_type}</td>
                    <td className="px-4 py-3">{d.quantity_expected}</td>
                    <td className="px-4 py-3">{d.quantity_actual}</td>
                    <td className="px-4 py-3 font-medium">{d.amount_difference?.toFixed(2)}</td>
                    <td className="px-4 py-3"><StatusBadge status={d.resolution_status} /></td>
                    <td className="px-4 py-3">{expandedId === d.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</td>
                  </tr>
                  {expandedId === d.id && (
                    <tr><td colSpan={8}><InlineEdit /></td></tr>
                  )}
                </tbody>
              ))}
            </table>
          </div>

          <div className="md:hidden space-y-3">
            {items.map(d => (
              <div key={d.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button onClick={() => toggleExpand(d)} className="w-full p-4 text-right">
                  <div className="flex items-center justify-between mb-2">
                    <StatusBadge status={d.resolution_status} />
                    <span className="font-medium text-gray-900">{d.supplier_invoices?.supplier_name_raw}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>{TYPE_LABELS[d.discrepancy_type] ?? d.discrepancy_type}</span>
                    <span>חשבונית {d.supplier_invoices?.invoice_number}</span>
                  </div>
                  <div className="flex gap-4 mt-2 text-xs text-gray-500">
                    <span>צפוי: {d.quantity_expected}</span>
                    <span>בפועל: {d.quantity_actual}</span>
                    <span>הפרש: {d.amount_difference?.toFixed(2)}</span>
                  </div>
                </button>
                {expandedId === d.id && <InlineEdit />}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
